/**
 * Session storage for the Passport bearer token.
 *
 * Kept outside React so the axios interceptor can tear the session down on a
 * 401 without importing a component. Listeners let the React tree react to
 * that teardown.
 */

const TOKEN_KEY = 'passport.token';
const USER_KEY = 'passport.user';

const listeners = [];

function safeGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    /* storage unavailable (private mode) — session stays in memory only */
  }
}

function safeRemove(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    /* nothing to do */
  }
}

function notify() {
  for (let i = 0; i < listeners.length; i++) {
    listeners[i]();
  }
}

export function getToken() {
  return safeGet(TOKEN_KEY);
}

export function getUser() {
  const raw = safeGet(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

export function setSession(token, user) {
  safeSet(TOKEN_KEY, token);
  safeSet(USER_KEY, JSON.stringify(user || {}));
  notify();
}

export function clearSession() {
  safeRemove(TOKEN_KEY);
  safeRemove(USER_KEY);
  notify();
}

/**
 * One-shot flag so the login page can confirm the deletion after the session
 * has been torn down and the router has bounced back to /login.
 */
const DELETED_KEY = 'passport.deleted';

export function markDeleted() {
  try {
    window.sessionStorage.setItem(DELETED_KEY, '1');
  } catch (e) {
    /* nothing to do */
  }
}

export function consumeDeletedFlag() {
  try {
    const value = window.sessionStorage.getItem(DELETED_KEY);
    window.sessionStorage.removeItem(DELETED_KEY);
    return value === '1';
  } catch (e) {
    return false;
  }
}

export function subscribe(listener) {
  listeners.push(listener);
  return function unsubscribe() {
    const index = listeners.indexOf(listener);
    if (index !== -1) listeners.splice(index, 1);
  };
}
